import React, { useEffect, useId, useRef, useState } from 'react';
import Button from './Button';
import {
  allowLabel, consentStatus, needsConsentNotice, onConsentChange, onConsentSettingsOpen, openConsentSettings,
  PRIVACY_POLICY_URL, setConsent,
} from '../lib/consent';
import s from './ConsentBanner.module.css';

// "Cookies" — the notice the marketing site shows too, in the same words, and
// answered once for both: the answer is a cookie on the parent domain
// (marketing/consent.js has the format and why it lives there).
//
// ── What it says ───────────────────────────────────────────────────────────
//
// Firebase Analytics in the app (#458, src/lib/analytics.js) and Google Ads
// conversion measurement on both sites (#754, src/lib/adConversions.js) run
// under one opt-out answer (#755; marketing/consent.js has the reasoning), so
// this is a notice. It says what runs — Google Ads on both sites, measuring
// whether an ad led to a sign-up or a subscription, which can include a hashed
// email address (enhanced conversions); Google Analytics in the app, counting
// screens and a few actions, and on the website, counting its pages (#783,
// marketing/adMeasurement.js); and the Meta Pixel on both sites, told each
// page viewed and the same conversions (#780) — what never happens, and how to
// turn it off. The full account is the privacy policy's section 4, one link
// away. It makes no claim about ads aimed at the visitor, since Meta may use
// the page views for that. The marketing site's banner
// (marketing/partials/footer.html) says the same.
//
// "OK" records the answer and keeps measurement on; "Decline" records it and
// turns measurement off in this page, at once. Two buttons of the same weight
// and nothing else on first showing, so it stays until answered. A browser
// sending Global Privacy Control with no answer on record is not shown it:
// measurement is already off, and "Cookie settings" says the signal was
// honoured. When what is in force is off, the first button reads "Allow"
// (marketing/consentBanner.js `allowLabel`).
//
// Not an MMS parity element: whether and how to ask is a matter of law. The
// wording and placement are ours, as TrialBanner.jsx records for itself.
//
// ── Where it sits ──────────────────────────────────────────────────────────
//
// In the page's flow under the topbar with the trial banner (AppShell), and
// under the form on the sign-in screen (LoginPage), so on a phone it moves the
// page down and covers no control; the app works whether or not it is
// answered. Not a dialog, and takes no focus when it appears by itself.
// "Cookie settings" reopens it — in the sidebar footer once signed in
// (ConsentSettingsButton below), and beside Privacy and Terms on the sign-in,
// check-your-email and thank-you screens (LegalLinks `cookieSettings`) — and
// then focus goes into it and back to the control afterwards. Under opt-out
// that control is the standing way to decline, so every screen carries one
// (#755).

export default function ConsentBanner({ inline = false }) {
  const [open, setOpen] = useState(() => needsConsentNotice());
  // True only when "Cookie settings" asked for it: that is when what is in
  // force is stated, when focus moves in, and when Escape may close it.
  const [reopened, setReopened] = useState(false);
  const region = useRef(null);
  const returnTo = useRef(null);
  const titleId = useId();

  useEffect(() => onConsentSettingsOpen(() => {
    returnTo.current = document.activeElement;
    setReopened(true);
    setOpen(true);
  }), []);

  // A choice made anywhere in this page closes every banner in it.
  useEffect(() => onConsentChange(() => {
    setOpen(false);
  }), []);

  useEffect(() => {
    if (open && reopened) region.current?.focus();
  }, [open, reopened]);

  if (!open) return null;

  const close = () => {
    const back = reopened && region.current?.contains(document.activeElement) ? returnTo.current : null;
    setOpen(false);
    setReopened(false);
    returnTo.current = null;
    back?.focus?.();
  };

  const choose = (choice) => {
    setConsent(choice);
    close();
  };

  return (
    <section
      ref={region}
      className={inline ? `${s.banner} ${s.inline}` : s.banner}
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && reopened && !needsConsentNotice()) close();
      }}
    >
      <div className={s.text}>
        <h2 className={s.title} id={titleId}>Cookies</h2>
        <p className={s.body}>
          This app and our website use cookies to measure our ads and how the app and
          the site are used. Google Ads and Meta tell us whether an ad led to a sign-up
          or a subscription, which can include a hashed form of your email address, Meta
          is also told which of our pages you visit, and Google Analytics counts which
          screens of the app and pages of our website are visited, and a few actions in
          the app. Never names, amounts or notes. Decline
          turns this off in the app and on our website, and Cookie settings changes your
          answer later.{' '}
          <a href={PRIVACY_POLICY_URL}>Privacy policy</a>
        </p>
        {reopened ? <p className={s.status}>{consentStatus()}</p> : null}
      </div>
      <div className={s.actions}>
        <Button variant="secondary" size="sm" onClick={() => choose('granted')}>{allowLabel()}</Button>
        <Button variant="secondary" size="sm" onClick={() => choose('denied')}>Decline</Button>
      </div>
    </section>
  );
}

// "Cookie settings", in the sidebar footer beside Sign out rather than on
// Business Settings: everything there is a property of the studio, written to
// Firestore and the same on every device, while this answer lives in this
// browser's cookies and does not sync. The sidebar footer holds what belongs
// to this seat, reachable from every screen. Off the app's frame, where there
// is no sidebar, the same control is LegalLinks' `cookieSettings` entry.
export function ConsentSettingsButton() {
  return (
    <button type="button" className="nav-item" onClick={openConsentSettings}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="nav-icon" aria-hidden="true">
        <path d="M8 1.5a6.5 6.5 0 106.4 5.3 2 2 0 01-2.4-2.4A2 2 0 019.7 2 6.5 6.5 0 008 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <circle cx="5.5" cy="7" r="0.9" fill="currentColor"/>
        <circle cx="8" cy="10.5" r="0.9" fill="currentColor"/>
        <circle cx="10.5" cy="8.5" r="0.9" fill="currentColor"/>
      </svg>
      Cookie settings
    </button>
  );
}
